import { useLayoutEffect } from 'react';

const CANVAS_WIDTH = 390;
// От breakpoints.lg (768) — от этой ширины окно уже не телефон, а десктоп
// с браузером вокруг: холст здесь ниже, 390×745, а не растянутые вниз
// 390×902 — тот же приём, что мобильный размер, но собственная высота,
// а не пересчёт от 902 (тот давал двойное сжатие: усыхала не только
// высота, но и ширина вместе с ней, включая иконки).
// AppLayout.module.scss переключает `.layout` на ту же высоту по той же
// точке — здесь и там должно совпадать.
const DESKTOP_MIN_WIDTH = 768;
const DESKTOP_HEIGHT = 745;

interface AppLayoutBox {
  scale: number;
  offsetX: number;
  offsetY: number;
  /** Высота холста В ЕГО СИСТЕМЕ КООРДИНАТ, до умножения на scale. */
  canvasHeight: number;
}

/**
 * Раскладывает холст 390 CSS-пикселей шириной в реальный вьюпорт через
 * `transform` (AppLayout.module.scss объясняет, почему не `zoom`).
 *
 * НА ТЕЛЕФОНЕ холст занимает ВСЮ ширину экрана, а высота подстраивается
 * под оставшееся место. Раньше здесь было вписывание целиком, как
 * `object-fit: contain`, с потолком `Math.min(1, …)`: холст никогда
 * не рос выше 390px, и на экране 430px по бокам оставалось по 20px
 * пустого фона — приложение выглядело узкой колонкой.
 *
 * Масштабировать по ширине, оставив высоту холста фиксированной (902),
 * было нельзя: на 390×844 холст стал бы выше вьюпорта, а нижний бар
 * и хэдер — это `position: absolute` ВНУТРИ холста, и бар уехал бы
 * за нижний край под `#root { overflow: hidden }`. Поэтому высота
 * холста считается здесь и отдаётся в `--app-canvas-height`:
 * бар остаётся прижат к низу экрана, а контент скроллится внутри
 * `.main`, который и так `scroll-y`.
 *
 * НА ДЕСКТОПЕ (от 768) поведение прежнее — вписывание без увеличения:
 * там вокруг мини-аппа окно браузера, и растянутый на 1440px холст
 * выглядел бы нелепо.
 *
 * Раньше `.layout` центрировался через `margin: 0 auto`, а масштаб считал
 * CSS-калькулятор `min(...)`. У обоих способов один и тот же изъян:
 * результат — произвольная дробь CSS-пикселей. Devtools показал
 * `margin: 0px 79.5px` — центрирование на половину пикселя. На части
 * сочетаний viewport/DPI Chromium рисует маску border-radius у
 * header/bottom_bar чуть мимо этой дробной сетки, и сквозь скругление
 * на долю пикселя просвечивает фон страницы.
 *
 * Здесь оба числа — масштаб и позиция — считаются так, чтобы результат
 * (`CANVAS_HEIGHT * scale`, положение левого/верхнего края) был целым
 * числом ФИЗИЧЕСКИХ пикселей экрана (с учётом `devicePixelRatio`), то
 * есть без остатка на любой дробной части.
 */
function computeBox(bannerHeight: number): AppLayoutBox {
  const dpr = window.devicePixelRatio || 1;
  const snap = (value: number): number => Math.round(value * dpr) / dpr;

  const availableWidth = window.innerWidth;
  const availableHeight = window.innerHeight - bannerHeight;

  if (availableWidth >= DESKTOP_MIN_WIDTH) {
    const canvasHeight = DESKTOP_HEIGHT;
    const rawScale = Math.min(
      1,
      availableWidth / CANVAS_WIDTH,
      availableHeight / canvasHeight,
    );
    // Вниз, а не round: гарантирует, что холст не станет ни на физический
    // пиксель больше доступного места.
    const scale = Math.floor(rawScale * canvasHeight * dpr) / (canvasHeight * dpr);
    return {
      scale,
      canvasHeight,
      offsetX: snap((availableWidth - CANVAS_WIDTH * scale) / 2),
      // Центр по вертикали в оставшемся под баннером пространстве, а не
      // прижато к его нижнему краю — на широких окнах холст иначе
      // повисал бы у самого верха с пустотой снизу.
      offsetY: snap(
        bannerHeight + Math.max(0, (availableHeight - canvasHeight * scale) / 2),
      ),
    };
  }

  // Телефон: ширина занята целиком. Масштаб округляем так, чтобы
  // `CANVAS_WIDTH * scale` попадал в целое число ФИЗИЧЕСКИХ пикселей —
  // иначе край холста рисуется мимо пиксельной сетки и сквозь скругления
  // просвечивает фон страницы (та же причина, что была у центрирования).
  const scale =
    Math.round((availableWidth / CANVAS_WIDTH) * CANVAS_WIDTH * dpr) /
    (CANVAS_WIDTH * dpr);

  // Высота холста подстраивается под экран и тоже ложится на целые
  // физические пиксели.
  const canvasHeight = Math.floor(availableHeight * dpr) / dpr / scale;

  return { scale, canvasHeight, offsetX: 0, offsetY: snap(bannerHeight) };
}

function getBannerHeight(): number {
  return (
    document.querySelector<HTMLElement>('[data-testid="demo-banner"]')
      ?.offsetHeight ?? 0
  );
}

/** Ставит `--app-scale`/`--app-offset-x`/`--app-offset-y` и следит за размером. */
export function useAppScale(): void {
  useLayoutEffect(() => {
    const apply = (): void => {
      const box = computeBox(getBannerHeight());
      const root = document.documentElement.style;
      root.setProperty('--app-scale', String(box.scale));
      root.setProperty('--app-offset-x', `${String(box.offsetX)}px`);
      root.setProperty('--app-offset-y', `${String(box.offsetY)}px`);
      root.setProperty('--app-canvas-height', `${String(box.canvasHeight)}px`);
    };
    apply();

    window.addEventListener('resize', apply);

    const banner = document.querySelector('[data-testid="demo-banner"]');
    const observer = banner ? new ResizeObserver(apply) : null;
    if (banner) observer?.observe(banner);

    return () => {
      window.removeEventListener('resize', apply);
      observer?.disconnect();
    };
  }, []);
}
